/**
 * contactService.js - Contact form validation and submission service.
 * Connects directly to Supabase contact_messages table and formats notifications.
 */

import { supabase, isSupabaseConfigured } from './supabase.js';

/**
 * Validates contact form input fields.
 * @param {Object} input - Form input object
 * @returns {{ valid: boolean, error: string, errors: string[], data?: Object }}
 */
export function validateContactInput({ name, email, role, subject, message } = {}) {
  const errors = [];

  const cleanName = typeof name === 'string' ? name.trim() : '';
  const cleanEmail = typeof email === 'string' ? email.trim() : '';
  const cleanRole = typeof role === 'string' ? role.trim() : '';
  const cleanSubject = typeof subject === 'string' ? subject.trim() : '';
  const cleanMessage = typeof message === 'string' ? message.trim() : '';

  if (!cleanName || cleanName.length < 2) {
    errors.push('Name must be at least 2 characters.');
  } else if (cleanName.length > 100) {
    errors.push('Name must not exceed 100 characters.');
  }

  if (!cleanEmail) {
    errors.push('Email is required.');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    errors.push('Please enter a valid email address.');
  }

  if (message !== undefined && message !== null) {
    if (!cleanMessage) {
      errors.push('Message is required.');
    } else if (cleanMessage.length > 5000) {
      errors.push('Message exceeds character limit of 5000.');
    } else if (cleanMessage.length < 10) {
      errors.push('Message must be at least 10 characters.');
    }
  }

  if (subject !== undefined && subject !== null && cleanSubject.length > 200) {
    errors.push('Subject must not exceed 200 characters.');
  }

  const valid = errors.length === 0;

  return {
    valid,
    error: errors.length > 0 ? errors[0] : null,
    errors,
    data: valid
      ? {
        name: cleanName,
        email: cleanEmail.toLowerCase(),
        role: cleanRole || 'Singer / Rapper',
        subject: cleanSubject || 'General Inquiry',
        message: cleanMessage,
      }
      : undefined,
  };
}

/**
 * Formats a contact form submission into an email payload object.
 * @param {Object} submission - Contact form fields
 * @returns {{ to: string, subject: string, text: string }}
 */
export function formatContactEmail({ name, email, role, subject, message, userId } = {}) {
  const emailSubject = `SenritsuX Contact Form: ${subject || 'General Inquiry'}`;
  const text = [
    `SenritsuX Contact Form Submission`,
    `-----------------------------------`,
    `Name: ${name || '(unknown)'}`,
    `Email: ${email || '(unknown)'}`,
    `Role: ${role || '(unknown)'}`,
    `User ID: ${userId || '(guest)'}`,
    `Subject: ${subject || '(none)'}`,
    ``,
    `Message:`,
    `${message || '(empty)'}`,
    ``,
    `-----------------------------------`,
    `Destination: senritsux.in@gmail.com`,
  ].join('\n');

  return {
    to: 'senritsux.in@gmail.com',
    subject: emailSubject,
    text,
  };
}

/**
 * Submits a contact message to the Supabase database.
 * @param {Object} payload - { name, email, role, subject, message }
 * @param {Object} options - { userId }
 * @returns {Promise<{ success: boolean, message: string, error?: string }>}
 */
export async function submitContactMessage(payload, { userId = null } = {}) {
  const validation = validateContactInput(payload);
  if (!validation.valid) {
    return {
      success: false,
      error: validation.error || 'Please correct the highlighted errors.',
    };
  }

  const record = {
    name: validation.data.name,
    email: validation.data.email,
    role: validation.data.role,
    subject: validation.data.subject,
    message: validation.data.message,
    user_id: userId || null,
    status: 'unread',
    email_status: 'pending',
  };

  try {
    const { error: dbError } = await supabase
      .from('contact_messages')
      .insert([record]);

    if (dbError) {
      console.error('[contactService] Supabase insert error:', dbError);
      return {
        success: false,
        error: 'Unable to deliver your message at this moment. Please try again shortly.',
      };
    }

    return {
      success: true,
      message: 'Message sent successfully. Our artist relations team will get back to you within 24 hours.',
    };
  } catch (err) {
    console.error('[contactService] Unexpected submission error:', err);
    return {
      success: false,
      error: 'An unexpected connection error occurred. Please try again.',
    };
  }
}
